import { unreachable } from "@mdotm/utils/runtime";
import {
	type IconName,
	type InnerRefProps,
	type StylableProps,
	type TransitionStatus,
	Icon,
	NestedTransition,
	Row,
	ScrollWrapper,
	StackingContext,
	Text,
	Transition,
} from "@mdotm/shadcn/legacy/components";
import { type DataAttributesProps, type ButtonPalette, Button, IconButton } from "@mdotm/shadcn/mdotm/components";
import {
	type NodeOrFn,
	overrideClassList,
	overrideClassName,
	renderNodeOrFn,
	toClassName,
} from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import { FocusScope } from "@radix-ui/react-focus-scope";
import { createContext, useMemo, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Divider } from "../Divider";
import type { XOR } from "@mdotm/utils/objects";

export const sidePanelTransitionDuration = 300;

function OverlaySidePanel({
	open,
	position = "left",
	onClose,
	width = 440,
	children,
	onAnimationStateChange,
	className,
	style,
}: Extract<SidePanelProps, { mode: "overlay" }>) {
	return (
		<StackingContext.Consumer>
			{({ zIndex }) =>
				createPortal(
					<Transition
						onChange={onAnimationStateChange}
						in={open}
						unmountOnExit={false}
						duration={sidePanelTransitionDuration}
					>
						<NestedTransition
							className="fixed inset-0 z-0 bg-black/20 transition-opacity duration-300"
							enterFromClassName="opacity-0"
							enterToClassName="opacity-100"
							unmountOnExit
							style={{
								zIndex,
							}}
						>
							{({ className: animatedClassList, style: animatedStyle, transitionStatus }) => (
								<>
									{/** biome-ignore lint/a11y/noStaticElementInteractions: migrate using hook for onclick outside and close it */}
									<div
										role="presentation"
										data-transitionstatus={transitionStatus}
										className={toClassName(animatedClassList)}
										style={animatedStyle}
										onClick={() => onClose?.()}
									/>
								</>
							)}
						</NestedTransition>
						<NestedTransition
							className={overrideClassList(
								"fixed top-0 bottom-0 max-w-full transition-all transition-discrete ease-in-out duration-300 flex flex-col overscroll-contain",
								className,
							)}
							// enterFromClassName="-translate-y-full scale-x-[2]" // vertical
							// enterToClassName="translate-y-0 scale-x-[1] shadow-xl"
							enterFromClassName={position === "right" ? "translate-x-full" : "-translate-x-full"} // horizontal
							enterToClassName="translate-x-0 shadow-xl" // horizontal
							style={{
								zIndex,
								width,
								top: 0,
								left: position === "left" ? 0 : undefined,
								right: position === "right" ? 0 : undefined,
								...style,
							}}
							exitedClassName="invisible"
							unmountOnExit
						>
							{({ className: animatedClassList, style: animatedStyle, transitionStatus }) => (
								// Not `trapped`: floating content spawned above the panel — a nested modal, a select, a date
								// picker — portals to the document body, and a trapped scope pulls focus back out of it.
								<FocusScope asChild loop>
									<div
										role="dialog"
										aria-modal="true"
										data-qualifier="OverlaySidePanel"
										data-transitionstatus={transitionStatus}
										className={toClassName(animatedClassList)}
										style={animatedStyle}
										onKeyDown={(event) => {
											if (event.key !== "Escape" || !onClose) {
												return;
											}
											// Dropdowns and date pickers portal their content to the body, so they are React
											// descendants of this panel but not DOM ones, and their Escape reaches here first.
											// It belongs to them: FloatingContent closes itself from its own document listener.
											if (!event.currentTarget.contains(event.target as Node)) {
												return;
											}
											event.preventDefault();
											onClose();
										}}
									>
										<StackingContext.Provider value={{ zIndex: zIndex + 1 }}>{children}</StackingContext.Provider>
									</div>
								</FocusScope>
							)}
						</NestedTransition>
					</Transition>,
					document.body,
				)
			}
		</StackingContext.Consumer>
	);
}

function InlineSidePanel({
	open,
	disableTransition,
	className,
	onClose,
	position = "left",
	width = [24, 364],
	toggleOffset = 28,
	style,
	children,
	toggleIcon = "Left",
	onChangeToggle,
	toggleDisabled,
}: Extract<SidePanelProps, { mode: "inline" }>) {
	const [smallWidth, expandedWidth] = width;
	return (
		<Transition
			in={open}
			className={{ "shrink-0 relative z-10": true, "transition-[width]": !disableTransition }}
			enterFromStyle={{ width: smallWidth }}
			enterToStyle={{ width: expandedWidth }}
		>
			{({ style, className }) => (
				<div
					data-qualifier="InlineSidePanel"
					className={overrideClassName("relative z-0", overrideClassList(className, className))}
					style={{ ...style, ...style }}
				>
					<div
						className={toClassName({
							"absolute rounded-r-[4px] z-10 flex items-center justify-center": true,
							"right-[-20px] translate-x-full": position === "left",
							"left-[-56px] translate-x-full": position === "right",
						})}
						style={{
							top: toggleOffset ?? 28,
							backgroundColor: themeCSSVars.palette_N20,
							width: 36,
							height: 36,
							...(position === "left"
								? { boxShadow: "10px 0px 16px -1px rgb(0 0 0 / 0.1)" }
								: position === "right"
									? { boxShadow: "-10px 0px 16px -1px rgb(0 0 0 / 0.1)" }
									: undefined),
						}}
						data-qualifier={`SideDrawer/Status(${open ? "expanded" : "closed"})`}
					>
						<div
							className={toClassName({
								"absolute pointer-events-none inset-y-0": true,
								"right-0 left-[14px] rounded-r-[4px]": position === "left",
								"right-[14px] left-0 rounded-l-[4px]": position === "right",
							})}
							style={{
								border: `2px solid ${themeCSSVars.palette_N100}`,
								marginTop: "-2px",
								marginBottom: "-2px",
								...(position === "left"
									? { borderLeft: "none" }
									: position === "right"
										? { borderRight: "none" }
										: undefined),
							}}
						/>
						<div>
							<NestedTransition
								className={{ "transition-transform": !disableTransition }}
								enterToClassName="scale-x-100"
								enterFromClassName="-scale-x-100"
							>
								{({ className: buttonClassList }) => (
									<IconButton
										onClick={() => onChangeToggle?.(open)}
										icon={toggleIcon}
										size={24}
										disabled={toggleDisabled}
										applyNegativeMargins
										color={themeCSSVars.palette_N400}
										className={buttonClassList}
										data-qualifier="SideDrawer/Toggle"
										style={{
											position: "relative",
										}}
									/>
								)}
							</NestedTransition>
						</div>
					</div>

					<div
						className="absolute inset-0 z-0 overflow-hidden"
						style={{
							boxShadow: "10px 0px 16px -1px rgb(0 0 0 / 0.1)",
							backgroundColor: themeCSSVars.palette_N20,
							border: `2px solid ${themeCSSVars.palette_N100}`,
							borderTopRightRadius: 0,
							borderBottomRightRadius: 0,
						}}
					>
						<NestedTransition
							className="transition-opacity"
							enterFromClassName="opacity-0"
							enterToClassName="opacity-100"
							exitedClassName="hidden"
						>
							{({ className: mainContentClassList }) => (
								<div className={overrideClassName(mainContentClassList, "absolute inset-y-0 right-0")}>
									<div className="flex flex-col h-full py-[10px]" style={{ width: expandedWidth }}>
										<ScrollWrapper
											outerContainerAppearance={{
												className: "grow",
											}}
											innerContainerAppearance={{
												className: "flex-1",
											}}
											className="px-4 flex-1 flex flex-col"
										>
											{children}
										</ScrollWrapper>
									</div>
								</div>
							)}
						</NestedTransition>
					</div>
				</div>
			)}
		</Transition>
	);
}

type Action = {
	label: string;
	palette?: ButtonPalette;
	disabled?: boolean;
} & XOR<
	[
		{
			onClick(ctx: SidePanelContextProps): void;
		},
		{
			onClickAsync(ctx: SidePanelContextProps): Promise<void>;
		},
	]
>;

export type SidePanelHeaderProps = {
	icon?: IconName | { name: IconName; onClick(): void; size?: string | number };
	title: string;
	primaryAction?: Action;
	secondaryAction?: Action;
	description?: string;
};

export function SidePanelHeader(props: SidePanelHeaderProps) {
	const ctx = useMandatoryContext(SidePanelContext);
	return (
		<div className="p-4" data-qualifier="SidePanelHeader">
			<Row
				justifyContent="space-between"
				alignItems="center"
				className={{ "mb-2 overflow-hidden": props.description !== undefined }}
			>
				<Row gap={2} alignItems="center" className="overflow-hidden">
					{typeof props.icon === "object" ? (
						<IconButton
							icon={props.icon.name}
							onClick={props.icon.onClick}
							size={props.icon.size ?? 24}
							color={themeCSSVars.palette_N500}
						/>
					) : typeof props.icon === "string" ? (
						<Icon icon={props.icon} size={24} color={themeCSSVars.palette_N500} />
					) : (
						<></>
					)}
					<Text type="Title/S" title={props.title} className="line-clamp-2">
						{props.title}
					</Text>
				</Row>
				<Row gap={2} flexGrow={0}>
					{props.secondaryAction && (
						<Button
							palette={props.secondaryAction.palette ?? "tertiary"}
							data-qualifier="SecondaryAction"
							size="small"
							disabled={props.secondaryAction.disabled}
							{...(props.secondaryAction.onClick
								? {
										onClick: () => props.secondaryAction!.onClick!(ctx),
									}
								: {
										onClickAsync: () => props.secondaryAction!.onClickAsync!(ctx),
									})}
						>
							{props.secondaryAction.label}
						</Button>
					)}
					{props.primaryAction && (
						<Button
							palette={props.primaryAction.palette ?? "primary"}
							data-qualifier="PrimaryAction"
							size="small"
							disabled={props.primaryAction.disabled}
							{...(props.primaryAction.onClick
								? {
										onClick: () => props.primaryAction!.onClick!(ctx),
									}
								: {
										onClickAsync: () => props.primaryAction!.onClickAsync!(ctx),
									})}
						>
							{props.primaryAction.label}
						</Button>
					)}
				</Row>
			</Row>
			{props.description && <Text type="Body/M/Medium">{props.description}</Text>}
		</div>
	);
}

export function SidePanelContent(props: { children: ReactNode } & StylableProps) {
	return (
		<div data-qualifier="SidePanelContent" className={overrideClassName("p-4", props.className)} style={props.style}>
			{props.children}
		</div>
	);
}

export type SidePanelFooterProps = InnerRefProps<HTMLDivElement> & {
	primaryAction?: NodeOrFn<SidePanelContextProps>;
	secondaryAction?: NodeOrFn<SidePanelContextProps>;
	neutralAction?: NodeOrFn<SidePanelContextProps>;
} & DataAttributesProps &
	StylableProps;

//TODO: specialize more this component when design is stable
export function SidePanelFooter({
	innerRef,
	neutralAction,
	primaryAction,
	secondaryAction,
	className,
	style,

	...dataAttrs
}: SidePanelFooterProps) {
	const ctx = useMandatoryContext(SidePanelContext);
	return (
		<div
			data-qualifier="SidePanelFooter"
			className={toClassName(overrideClassList("bg-white", className))}
			style={style}
		>
			<Divider />
			<div
				ref={innerRef}
				className={toClassName({
					flex: true,
					"justify-between p-4": true,
				})}
				{...dataAttrs}
			>
				<div className="mr-2">{renderNodeOrFn(neutralAction, ctx)}</div>
				<div className="space-x-2">
					{renderNodeOrFn(secondaryAction, ctx)}
					{renderNodeOrFn(primaryAction, ctx)}
				</div>
			</div>
		</div>
	);
}

export const SidePanelMode = {
	OVERLAY: "overlay",
	INLINE: "inline",
} as const;

export type SidePanelMode = (typeof SidePanelMode)[keyof typeof SidePanelMode];

export type SidePanelProps = StylableProps & {
	open: boolean;
	onClose?(): void;
	/** @default 'left'' */
	position?: "left" | "right";
	disableTransition?: boolean;
	children: ReactNode;
} & (
		| {
				mode: Extract<SidePanelMode, "overlay">;
				onAnimationStateChange?(state: TransitionStatus): void;
				width?: CSSProperties["width"];
		  }
		| {
				mode: Extract<SidePanelMode, "inline">;
				toggleIcon?: IconName;
				toggleDisabled?: boolean;
				onChangeToggle?(open: boolean): void;
				/**
				 * Describe the width behavior of side-panel
				 * [smallWidth, expandedWidth]
				 */
				width?: [number, number];
				/** @default 28 */
				toggleOffset?: number;
		  }
	);

type SidePanelContextProps = { open: boolean; onClose?(): void; mode: SidePanelMode };
const SidePanelContext = createContext<SidePanelContextProps | null>(null);

export function SidePanel(props: SidePanelProps) {
	const ctx = useMemo<SidePanelContextProps>(
		() => ({ onClose: props.onClose, mode: props.mode, open: props.open }),
		[props.mode, props.open, props.onClose],
	);

	switch (props.mode) {
		case "inline":
			return (
				<SidePanelContext.Provider value={ctx}>
					<InlineSidePanel {...props} />
				</SidePanelContext.Provider>
			);
		case "overlay":
			return (
				<SidePanelContext.Provider value={ctx}>
					<OverlaySidePanel {...props} />
				</SidePanelContext.Provider>
			);
		default:
			unreachable(props);
	}
}
